<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="my-4">
      <div>
        <h2 class="font-weight-regular pb-4"><span v-html="$t('page-rules.seasonal-rule.section-details.title')"></span> <span class="red--text">*</span></h2>
        <p v-html="$t('page-rules.seasonal-rule.section-details.subtitle')" class="mb-0"></p>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content class="text-right mb-4">
      <v-form ref="detailsform" v-model="valid" class="text-left">
        <v-row class="px-4">
          <v-col cols="12" class="pt-0 px-0 pb-0">
            <span v-html="$t('page-rules.seasonal-rule.section-details.fields.season-name.label')" class="subtitle-1 font-weight-regular"></span>
            <v-text-field :placeholder="$t('page-rules.seasonal-rule.section-details.fields.season-name.placeholder')" class="mt-4" required outlined dense solo-flat v-model="mutatableSeasonalBookingRule.label" :rules="labelRules"></v-text-field>
          </v-col>
        </v-row>
        <v-row class="px-4">
          <v-col cols="12" class="pt-0 px-0 pb-0">
            <span v-html="$t('page-rules.seasonal-rule.section-details.fields.season-period.label')" class="subtitle-1 font-weight-regular"></span>
            <v-row>
              <v-col :class="{'py-0': !seasonalBookingRuleDates.length}">
                <div v-for="(date, dateIndex) in seasonalBookingRuleDates" :key="date.id" class="mb-2">
                  <v-chip close class="mr-2" @click:close="removeDateFromRule(dateIndex)">{{ moment(date.dates.start).format('DD/MM/YYYY') }} - {{ moment(date.dates.end).format('DD/MM/YYYY') }}</v-chip>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="5">
                <v-menu v-model="fromDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :label="$t('page-rules.seasonal-rule.section-details.fields.season-period.from.label')"
                      :value="fromDateFormatted"
                      append-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    :allowed-dates="allowedDates"
                    v-model="fromDate"
                    @change="fromDateMenu = false; toDate = moment(fromDate).format('YYYY-MM-DD')"
                    :min="moment(new Date()).format('YYYY-MM-DD')"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="5">
                <v-menu v-model="toDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                    :label="$t('page-rules.seasonal-rule.section-details.fields.season-period.to.label')"
                      :value="toDateFormatted"
                      append-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!fromDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    v-model="toDate"
                    @change="toDateMenu = false"
                    :allowed-dates="allowedDatesFromStart"
                    :min="moment(fromDate).format('YYYY-MM-DD')"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" :disabled="!fromDate || !toDate" outlined @click="addDateToRule()" style="padding-top:19px; padding-bottom: 19px;">Add Date</v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
      <v-btn @click.stop="save()" v-html="$t('global-terms.save-continue')" color="primary" large :loading="saving" :disabled="saving"></v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import { mapActions, mapState } from 'pinia'
import { useBookingRuleStore } from '@/stores'
import moment from 'moment'
import { clone } from 'ramda'
import { sortObjects } from '@/helpers/stateUtils'

export default {
  data () {
    return {
      saving: false,
      valid: true,
      mutatableSeasonalBookingRule: this.clone(this.seasonalBookingRule),
      fromDateMenu: false,
      toDateMenu: false,
      fromDate: null,
      toDate: null,
      labelRules: [(v) => !!v || '*Season name is required.'],
    }
  },
  props: {
    place: {
      type: Object,
      required: true,
    },
    isCreate: {
      type: Boolean,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    seasonalBookingRule: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState(useBookingRuleStore, {
      seasonalBookingRuleFindByIds: 'findByIds',
    }),
    fromDateFormatted () {
      return this.fromDate ? moment(this.fromDate).format('DD/MM/Y') : ''
    },
    toDateFormatted () {
      return this.toDate ? moment(this.toDate).format('DD/MM/Y') : ''
    },
    seasonalBookingRuleDates () {
      return this.sortObjects(this.mutatableSeasonalBookingRule.seasonalBookingRuleDates, 'dates.start')
    },
    placeSeasonalBookingRuleDatesFlattened () {
      let flattenedDates = []

      // dates under this rule (done separately so we capture real-time events)
      this.mutatableSeasonalBookingRule.seasonalBookingRuleDates.forEach(sbrd => {
        let start = moment(sbrd.dates.start)
        let end = moment(sbrd.dates.end)
        let diff = end.diff(start, 'days')

        // add each day in range to array
        for (let i = 0; i <= diff; i++) {
          flattenedDates.push(start.clone().add(i, 'd').format('YYYY-MM-DD'))
        }
      })

      // all other seasonal booking rules under this place
      this.seasonalBookingRuleFindByIds(this.place.seasonalBookingRules).forEach(seasonalBookingRule => {
        if (seasonalBookingRule.id !== this.mutatableSeasonalBookingRule.id) {
          seasonalBookingRule.seasonalBookingRuleDates.forEach(sbrd => {
            let start = moment(sbrd.dates.start)
            let end = moment(sbrd.dates.end)
            let diff = end.diff(start, 'days')

            // add each day in range to array
            for (let i = 0; i <= diff; i++) {
              flattenedDates.push(start.clone().add(i, 'd').format('YYYY-MM-DD'))
            }
          })
        }
      })

      return flattenedDates
    },
  },
  methods: {
    sortObjects,
    allowedDates (date) {
      return !this.placeSeasonalBookingRuleDatesFlattened.includes(date)
    },
    allowedDatesFromStart (date) {
      let start = moment(this.fromDate)
      let end = moment(date)
      let diff = end.diff(start, 'days')

      // loop through each day since start date and return false at the first occurence of an unavailable date
      for (let i = 1; i <= diff; i++) {
        let incDay = start.clone().add(i, 'd')
        if (this.placeSeasonalBookingRuleDatesFlattened.includes(incDay.format('YYYY-MM-DD'))) {
          return false
        }
      }

      return true
    },
    addDateToRule () {
      this.mutatableSeasonalBookingRule.seasonalBookingRuleDates.push({
        dates: {
          start: this.fromDate,
          end: this.toDate,
        },
      })

      this.fromDate = null
      this.toDate = null
      this.$refs.detailsform.resetValidation()
    },
    removeDateFromRule (dateIndex) {
      this.mutatableSeasonalBookingRule.seasonalBookingRuleDates.splice(dateIndex, 1)
    },
    clone,
    ...mapActions(useBookingRuleStore, ['httpCreateSeasonalBookingRule', 'httpUpdateSeasonalBookingRule']),
    save () {
      console.log(this.$refs.detailsform.validate())
      if (this.$refs.detailsform.validate()) {
        this.saving = true

        const bookingRuleClone = this.clone(this.mutatableSeasonalBookingRule)

        if (this.isCreate) {
          const clonePlace = this.clone(this.place)

          // after save, display green notification and emit event to move onto next section
          this.httpCreateSeasonalBookingRule({ seasonalBookingRule: bookingRuleClone, place: clonePlace }).then((ruleId) => {
            this.saving = false

            // forward after saving first form
            this.$router.push({ name: 'place-rules-seasonal-edit', params: { ruleId } })

            if (!this.isFinalized) {
              this.$emit('autoScroll', 1)
              this.$nextTick(() => {
                this.$vuetify.goTo('#second', {
                  duration: 1000,
                  offset: 25,
                })
              })
            }
          })
        } else if (this.isEdit) {
          this.httpUpdateSeasonalBookingRule({ seasonalBookingRule: bookingRuleClone }).then(() => {
            this.saving = false
          })

          if (!this.isFinalized) {
            this.$emit('autoScroll', 1)
            this.$nextTick(() => {
              this.$vuetify.goTo('#second', {
                duration: 1000,
                offset: 25,
              })
            })
          }
        }
      }
    },
  },
  watch: {
    seasonalBookingRule (newVal) {
      this.mutatableSeasonalBookingRule = this.clone(newVal)
      if (this.isCreate) {
        this.$refs.detailsform.resetValidation()
      }
    },
  },
}
</script>
